import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useCtaSheenStyles } from '../hooks/useCtaSheenStyles';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Static slot group definitions (for contentSchema — must be inline literals)
// ============================================================================

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Explore Neighborhood CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/neighborhood' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const placeCard1Def: SlotGroupDefinition = {
  label: 'Neighborhood Photo 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[48].poi_featured_image' },
    title: { label: 'Place Name', type: 'text', defaultField: 'neighborhood_pois[48].poi_name' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'neighborhood_pois[48].poi_name',
      generator: {
        prompt: 'Alt text for this place photo. Describe the place itself, using its name, type, and description from the property data; do not invent visual details that data does not support.',
        length: 'one short phrase, under 125 characters',
      },
    },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 48,
    fieldMappings: { image: 'poi_featured_image', title: 'poi_name', alt: 'poi_name' },
  },
  generator: {
    prompt: 'A nearby place that the section body text names, shown with its photo.',
    context:
      'Photo 1 of 4 in the 2×2 grid beside the neighborhood paragraph. Must be a place mentioned in that paragraph, not the property itself. Do not reuse photos 2–4.',
  },
};

const placeCard2Def: SlotGroupDefinition = {
  label: 'Neighborhood Photo 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[50].poi_featured_image' },
    title: { label: 'Place Name', type: 'text', defaultField: 'neighborhood_pois[50].poi_name' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'neighborhood_pois[50].poi_name',
      generator: {
        prompt: 'Alt text for this place photo. Describe the place itself, using its name, type, and description from the property data; do not invent visual details that data does not support.',
        length: 'one short phrase, under 125 characters',
      },
    },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 50,
    fieldMappings: { image: 'poi_featured_image', title: 'poi_name', alt: 'poi_name' },
  },
  generator: {
    prompt: 'A different nearby place named in the section body text, shown with its photo.',
    context:
      'Photo 2 of 4 in the 2×2 grid beside the neighborhood paragraph. Must be a place mentioned in that paragraph, not the property itself. Different place from photos 1, 3, and 4.',
  },
};

const placeCard3Def: SlotGroupDefinition = {
  label: 'Neighborhood Photo 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[3].poi_featured_image' },
    title: { label: 'Place Name', type: 'text', defaultField: 'neighborhood_pois[3].poi_name' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'neighborhood_pois[3].poi_name',
      generator: {
        prompt: 'Alt text for this place photo. Describe the place itself, using its name, type, and description from the property data; do not invent visual details that data does not support.',
        length: 'one short phrase, under 125 characters',
      },
    },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 3,
    fieldMappings: { image: 'poi_featured_image', title: 'poi_name', alt: 'poi_name' },
  },
  generator: {
    prompt: 'Another nearby place named in the section body text, ideally a different kind of place than photos 1 and 2.',
    context:
      'Photo 3 of 4 in the 2×2 grid beside the neighborhood paragraph. Must be a place mentioned in that paragraph, not the property itself. Different place from photos 1, 2, and 4.',
  },
};

const placeCard4Def: SlotGroupDefinition = {
  label: 'Neighborhood Photo 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[4].poi_featured_image' },
    title: { label: 'Place Name', type: 'text', defaultField: 'neighborhood_pois[4].poi_name' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'neighborhood_pois[4].poi_name',
      generator: {
        prompt: 'Alt text for this place photo. Describe the place itself, using its name, type, and description from the property data; do not invent visual details that data does not support.',
        length: 'one short phrase, under 125 characters',
      },
    },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 4,
    fieldMappings: { image: 'poi_featured_image', title: 'poi_name', alt: 'poi_name' },
  },
  generator: {
    prompt: 'A remaining nearby place named in, or located on a street named in, the section body text.',
    context:
      'Photo 4 of 4 in the 2×2 grid beside the neighborhood paragraph. Not the property itself. Different place from photos 1–3.',
  },
};

// ============================================================================
// Place photo card
// ============================================================================

interface PlaceCardProps {
  image: string | null;
  title: string | null;
  alt: string | null;
}

function PlaceCard({ image, title, alt }: PlaceCardProps) {
  return (
    <div className="neighborhood-grid-cell">
      {image ? (
        <VibesitesImage
          url={image}
          alt={alt || title || ''}
          className="neighborhood-grid-cell__img"
          sizes="(min-width: 1024px) 25vw, 50vw"
        />
      ) : (
        <div className="absolute inset-0 bg-white" aria-hidden="true" />
      )}
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function NeighborhoodSection() {
  const cta = useSlotGroup('neighborhoodsection.cta_link', ctaLinkDef);
  const place1 = useSlotGroup('neighborhoodsection.place_1', placeCard1Def);
  const place2 = useSlotGroup('neighborhoodsection.place_2', placeCard2Def);
  const place3 = useSlotGroup('neighborhoodsection.place_3', placeCard3Def);
  const place4 = useSlotGroup('neighborhoodsection.place_4', placeCard4Def);

  useRevealStyles();
  useCtaSheenStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full overflow-hidden bg-primary py-10 md:py-12 lg:py-14"
    >
      <div className="mx-auto flex flex-col items-start gap-8 px-4 sm:px-6 lg:grid lg:max-w-none lg:grid-cols-2 lg:items-start lg:gap-6 lg:px-0 xl:gap-10">
        {/* Left: copy on white panel — bleeds to viewport left, ends at center on lg+ */}
        <div className="relative z-10 -mx-4 flex w-[calc(100%+2rem)] flex-col bg-white px-8 py-8 sm:-mx-6 sm:w-[calc(100%+3rem)] sm:px-10 sm:py-10 md:px-12 md:py-12 lg:mx-0 lg:w-full lg:px-12 lg:pr-14 xl:px-16 xl:pr-20">
          <RichTextContent
              path="neighborhoodsection.eyebrow"
              label="Eyebrow"
              defaultValue="<p>Ideally Located</p>"
              as="p"
              className="font-body mb-4 text-xs uppercase tracking-[0.25em] text-foreground sm:text-sm"
            />

            <RichTextContent
              path="neighborhoodsection.heading"
              label="Heading"
              defaultValue='<p><span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>'
              as="h2"
              className="font-heading mb-6 text-3xl font-bold uppercase leading-[1.1] text-foreground sm:text-4xl md:text-[2.75rem]"
            />

            <div className="mb-8 h-1 w-16 rounded-full bg-secondary" aria-hidden="true" />

            <RichTextContent
              path="neighborhoodsection.body"
              label="Body Text"
              defaultValue="<p>Experience the best of downtown living with easy access to dining, entertainment, and cultural destinations. Our prime location puts you at the center of it all while offering a peaceful retreat to call home.</p>"
              as="div"
              className="font-body mb-10 max-w-xl text-base leading-relaxed text-muted-foreground [&_a]:text-primary [&_a]:underline"
            />

            <EditableSlotGroup
              groupPath="neighborhoodsection.cta_link"
              definition={ctaLinkDef}
              className="inline-block"
            >
              <a
                href={cta.url || '/neighborhood'}
                target={cta._target || '_self'}
                className="neighborhood-cta vantage-cta-sheen inline-flex items-center justify-center px-8 py-3.5 font-heading text-sm uppercase tracking-wider"
              >
                <span className="relative z-10">
                  <RichTextContent
                    path="neighborhoodsection.cta_label"
                    label="CTA Label"
                    defaultValue="<p>Explore the Neighborhood</p>"
                    as="span"
                  />
                </span>
              </a>
          </EditableSlotGroup>
        </div>

        {/* Right: 2×2 grid of places named in the paragraph */}
        <div className="neighborhood-photo-grid relative z-0 grid w-full max-w-xl grid-cols-2 gap-2.5 sm:max-w-none sm:gap-3 lg:max-w-none lg:pl-4 lg:pr-8 xl:pr-12">
          <EditableSlotGroup groupPath="neighborhoodsection.place_1" definition={placeCard1Def} className="block">
            <PlaceCard image={place1.image} title={place1.title} alt={place1.alt} />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="neighborhoodsection.place_2" definition={placeCard2Def} className="block">
            <PlaceCard image={place2.image} title={place2.title} alt={place2.alt} />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="neighborhoodsection.place_3" definition={placeCard3Def} className="block">
            <PlaceCard image={place3.image} title={place3.title} alt={place3.alt} />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="neighborhoodsection.place_4" definition={placeCard4Def} className="block">
            <PlaceCard image={place4.image} title={place4.title} alt={place4.alt} />
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .neighborhood-cta [data-vibesite-richtext],
        .neighborhood-cta [data-vibesite-richtext] p,
        .neighborhood-cta [data-vibesite-richtext] span {
          margin: 0;
          font: inherit;
          color: inherit;
          text-transform: inherit;
          letter-spacing: inherit;
        }

        .neighborhood-grid-cell {
          position: relative;
          aspect-ratio: 1 / 1;
          width: 100%;
          overflow: hidden;
          background: #fff;
          border-radius: 4px;
        }
        .neighborhood-grid-cell__img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodSection',
  displayName: 'Neighborhood Section',
  tokens: {
    'neighborhoodsection.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Ideally Located</p>',
    },
    'neighborhoodsection.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'neighborhoodsection.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_home_neighborhood_blurb',
      defaultValue:
        '<p>Experience the best of downtown living with easy access to dining, entertainment, and cultural destinations. Our prime location puts you at the center of it all while offering a peaceful retreat to call home.</p>',
    },
    'neighborhoodsection.cta_label': {
      label: 'CTA Label',
      type: 'richtext',
      defaultValue: '<p>Explore the Neighborhood</p>',
    },
  },
  slotGroups: {
    'neighborhoodsection.cta_link': ctaLinkDef,
    'neighborhoodsection.place_1': placeCard1Def,
    'neighborhoodsection.place_2': placeCard2Def,
    'neighborhoodsection.place_3': placeCard3Def,
    'neighborhoodsection.place_4': placeCard4Def,
  },
};

(NeighborhoodSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodSection;
